import type {
  ClinicalSectionSummary,
  ClinicalSummaryItem,
  ClinicalSummarySectionSlug,
  LockReason,
  SectionConfig,
  SectionSlug,
} from "@/api/hooks";
import type {
  AdoptionPin,
  LibraryAsset,
  LibraryAssetType,
  PathwaySelection,
} from "@/api/library";

import { adoptionLock, isBehind } from "./adoptions";
import { isLocked, lockReasonOf } from "./fieldState";
import {
  ASSET_TYPE_LABELS,
  sectionForAssetType,
  type LibrarySectionSlug,
} from "./librarySections";

/**
 * What ONE clinical section adopted from the shared library, as arithmetic
 * rather than as a screen (#443).
 *
 * ## Why this module exists beside `lib/adoptions.ts`
 *
 * `adoptions.ts` answers "what does this PROJECT pin", organised by asset —
 * #372's question. This one answers "what supplies the section I am standing
 * in", organised by section, which is #36's "in-context adoption detail" and
 * the question a clinician reading `alerts` actually has. Neither is derivable
 * from the other in the browser, because the join lives in the service's own
 * section projection.
 *
 * ## THE ADOPTED IDENTITY LIVES IN THREE PLACES, ONE PER SECTION SHAPE
 *
 * This is the whole reason the module is not four lines long, and it is stated
 * outright by `services/editor_service/section_registry.py:200-207`: "`rules`
 * does (the exact-file claim); `alerts` **never** (born-editor, pins only);
 * `pathways` claims its scope for attribution but serves refs only."
 *
 * | Section | Authoritative source |
 * | --- | --- |
 * | `alerts` | `AlertsSection.matrices: AlertMatrixRef[]` |
 * | `pathways` | `PathwaysSection.pathways: PathwayRef[]` (carries `variantOf`) |
 * | the other five | `FieldState.origin = {kind: "library_adoption", ref, version}` |
 *
 * `adoptionProvenanceByAsset` inverts only the third. Reusing it alone renders
 * "this section adopts nothing" for `alerts` — the clinical guard-floor section,
 * which serves no fields at all — and for `pathways`, which serves refs only.
 * `sectionAdoptedItems` reads all three, and `lib/sectionAdoption.test.ts`
 * asserts that neither section comes back empty.
 *
 * ## WHAT THIS MODULE MAY NOT DO
 *
 * It may not compare content, and it may not decide editorial state. "Locally
 * overridden" is `FieldState.provenance` — the SERVER's reading, reconciled by
 * `lib/fieldState.ts` — never a browser-side diff of a value against a library
 * body, which is one of the ticket's own non-goals. "Outdated" is `isBehind`
 * against the catalog's `latestVersion`, the same arithmetic #372's surface
 * uses, so the two surfaces cannot disagree about whether a pin has been left
 * behind. Every version rendered is a value the service returned; nothing here
 * derives, orders or increments one.
 */

/**
 * Which of the three shapes carries a section's adopted identity.
 *
 * Reported rather than inferred from what a response happens to contain: a
 * section that legitimately adopts nothing and a section whose identity was
 * read from the wrong place both produce an empty list, and only the first is
 * ever a true statement.
 */
export type AdoptionIdentitySource =
  /** `AlertsSection.matrices` — born-editor, pins only, never fields. */
  | "alert_matrices"
  /** `PathwaysSection.pathways` — refs only, carrying `variantOf`. */
  | "pathway_refs"
  /** `FieldState.origin`, for the five sections that serve fields. */
  | "field_origin";

export const IDENTITY_SOURCE_BY_SECTION = {
  alerts: "alert_matrices",
  pathways: "pathway_refs",
  "bg-sets": "field_origin",
  "chief-complaint": "field_origin",
  rules: "field_origin",
  safety: "field_origin",
  summary: "field_origin",
} as const satisfies Record<SectionSlug, AdoptionIdentitySource>;

export function adoptionIdentitySource(
  section: SectionSlug,
): AdoptionIdentitySource {
  return IDENTITY_SOURCE_BY_SECTION[section];
}

/** One shared-library item the section itself reports as supplying it. */
export type SectionAdoptedItem = {
  readonly assetId: string;
  /**
   * The version the section reported, exactly as it arrived.
   *
   * `null` only where the contract allows it: `FieldOrigin.version` is optional
   * and nullable, so an attribution can name the item without naming a version.
   * Rendered as "version not reported" rather than as a guess.
   */
  readonly version: string | null;
  /** Parent pathway asset id where the contract carries one, else `null`. */
  readonly variantOf: string | null;
  /**
   * The SERVER's persisted Always/Dynamic state for this pin, or `null`.
   *
   * `PathwayRef.selection` (#642), carried through unchanged. `null` is a real
   * answer on the Pathways section and means what the contract says it means —
   * "null for non-Pathway pins and for Pathway variants that are summary-only
   * rather than active selections" — so a surface renders it as a stated fact
   * rather than as a missing value.
   *
   * IT IS NEVER COMPUTED HERE. `authority` has one legal value, `server`, and
   * the policy that derives mode and order publishes no request-shaped field:
   * preflight reads the effective engine-owned registration and persistence
   * returns the result. A browser that inferred a mode from a coordinate, a
   * name or a position in this list would be asserting an authority the
   * contract states it does not hold.
   */
  readonly selection: PathwaySelection | null;
  readonly source: AdoptionIdentitySource;
  /**
   * Field paths this item supplied, in response order.
   *
   * Empty for the two refs-only sections, and that emptiness is a fact about
   * the SHAPE rather than about the adoption — `alerts` serves no fields at all.
   */
  readonly fieldPaths: readonly string[];
  /** The lock the service reported on the values it supplied, when it did. */
  readonly lock: LockReason | null;
};

/**
 * Every shared-library item one section reports, read from whichever of the
 * three shapes is authoritative for it.
 *
 * Order is the response's, deduplicated by asset id on first appearance: a
 * projection may attribute many fields to one adoption, and a clinician wants
 * one row per item rather than one per field.
 */
export function sectionAdoptedItems(
  section: SectionConfig,
): readonly SectionAdoptedItem[] {
  if (section.section === "alerts") {
    return section.matrices.map((matrix) => ({
      assetId: matrix.assetId,
      version: matrix.version,
      variantOf: null,
      selection: null,
      source: "alert_matrices" as const,
      fieldPaths: [],
      lock: null,
    }));
  }

  if (section.section === "pathways") {
    return section.pathways.map((pathway) => ({
      assetId: pathway.assetId,
      version: pathway.version,
      variantOf: pathway.variantOf ?? null,
      // The one section whose contract carries a selection at all. `?? null`
      // rather than a default mode: an absent field means the service did not
      // report one, and inventing `dynamic` here would put a runtime claim on
      // screen that no response supports.
      selection: pathway.selection ?? null,
      source: "pathway_refs" as const,
      fieldPaths: [],
      lock: null,
    }));
  }

  const byAsset = new Map<
    string,
    { version: string | null; fieldPaths: string[]; lock: LockReason | null }
  >();

  for (const field of section.fields) {
    const { origin } = field;
    if (!origin || origin.kind !== "library_adoption") {
      continue;
    }
    const entry = byAsset.get(origin.ref) ?? {
      version: origin.version ?? null,
      fieldPaths: [],
      lock: null,
    };
    entry.fieldPaths.push(field.path);
    // First reported lock wins, and any lock wins over none — the rule
    // `adoptionProvenanceByAsset` already applies, for the reason it states:
    // ADR 0001 has no "unless another field of the same adoption was editable"
    // clause.
    entry.lock ??= isLocked(field) ? (lockReasonOf(field) ?? null) : null;
    byAsset.set(origin.ref, entry);
  }

  return [...byAsset].map(([assetId, entry]) => ({
    assetId,
    version: entry.version,
    variantOf: null,
    // `FieldOrigin` carries no selection, and the five sections that serve
    // fields are not the Pathways one. `null` is the contract's answer for a
    // non-Pathway pin, not a placeholder.
    selection: null,
    source: "field_origin" as const,
    fieldPaths: entry.fieldPaths,
    lock: entry.lock,
  }));
}

/**
 * The paths this project has changed away from what it adopted, per the SERVER.
 *
 * `overridden` and `added` are the contract's two divergences from the adopted
 * base — the same pair `fieldProvenance` maps onto the shell's presentation —
 * and reading them here is what makes the ticket's "no browser-side content
 * diffing" non-goal true by construction rather than by care. A `locked` field
 * is deliberately absent: a lock is not a divergence, and ADR 0001's resolution
 * of a lock/provenance disagreement is toward locked.
 */
export function overriddenFieldPaths(
  section: SectionConfig,
): readonly string[] {
  return section.fields
    .filter(
      (field) =>
        !isLocked(field) &&
        (field.provenance === "overridden" || field.provenance === "added") &&
        // A value an ADOPTED library version supplied is the pin's, not the
        // draft's: `added` carries the adoption's origin at a path the base
        // does not ship (an authored Summary prompt at a new key), and the
        // server drops the origin for every draft-supplied value. Counting it
        // here called an adoption a local override — "This project's draft
        // supplies one value" beside a pin that supplied it.
        field.origin?.kind !== "library_adoption",
    )
    .map((field) => field.path);
}

/**
 * The six states the ticket requires a clinician to be able to tell apart.
 *
 * `not-applicable` is a real answer, not an absence: a section that serves no
 * fields and carries no refs adopts nothing, and saying so is different from
 * failing to find out.
 */
export type SectionAdoptionState =
  | "current"
  | "converging"
  | "outdated"
  | "overridden"
  | "unavailable"
  | "not-applicable";

/** Why the state could not be determined. `null` unless the state is `unavailable`. */
export type AdoptionUnavailableReason =
  /** `GET /sections/{section}` did not answer, so no identity is known at all. */
  | "projection_unread"
  /** The catalog did not answer, so no item's head version is known. */
  | "library_unread"
  /** The catalog answered but does not list an item the section named. */
  | "library_incomplete"
  /**
   * The section named an item but no version for it.
   *
   * `FieldOrigin.version` is optional and nullable in the contract, so this is
   * a legal response rather than a malformed one — and a legal response that
   * establishes NOTHING about currency. Reported as unavailable rather than
   * folded into `current`, because "this project adopts the library's head" is
   * a claim, and the only evidence for it would be a version the service
   * declined to name.
   */
  | "version_unreported";

/** One adopted item, joined to what the catalog and the pin set say about it. */
export type SectionAdoptedItemView = SectionAdoptedItem & {
  /** The catalog row naming it, absent when the library did not return one. */
  readonly asset: LibraryAsset | undefined;
  /** The library's current head, or `null` when the catalog did not name it. */
  readonly latestVersion: string | null;
  /** Whether a newer immutable version exists. `false` when the head is unknown. */
  readonly behind: boolean;
  /**
   * The version this project's own pin set carries for the item, or `null`.
   *
   * The section projection and `GET /adoptions` are one subject read from two
   * ends. Carrying both is what lets the surface say they agree instead of
   * quietly showing one and implying the other.
   */
  readonly pinnedVersion: string | null;
  /**
   * The pin's family as the pin set itself reports it, or `null` when the pin
   * set was not read or does not name this item.
   *
   * The catalog's fact, joined onto the pin server-side (#615) — so an item's
   * exact library route is known from the stored pin alone, and survives a
   * catalog read that failed. Never inferred from the section this item was
   * read in: that is the projection, and the ratified contract rules it out
   * as an identity source.
   */
  readonly pinnedAssetType: LibraryAssetType | null;
  /**
   * The server's lock on this adoption: the projection's reported lock, or the
   * catalog's own guard-floor declaration. `adoptionLock`'s resolution, reused
   * rather than restated.
   */
  readonly effectiveLock: LockReason | null;
};

export type SectionAdoption = {
  readonly state: SectionAdoptionState;
  readonly reason: AdoptionUnavailableReason | null;
  /** Empty only when nothing is known or nothing is adopted. */
  readonly items: readonly SectionAdoptedItemView[];
  /** Paths the server reported as locally changed, whatever the headline state. */
  readonly overriddenPaths: readonly string[];
  /** The section's identity shape, so a surface can say where it read from. */
  readonly source: AdoptionIdentitySource;
  /** The pin-set token the section projection was computed from. */
  readonly sectionAdoptionsVersion: string | null;
  /** The current project pin-set token read from `GET /adoptions`. */
  readonly pinSetVersion: string | null;
  /**
   * Whether `GET /adoptions` answered.
   *
   * Carried separately because `pinnedVersion: null` means two different things
   * and only one of them is a fact: the pin set was read and does not name this
   * item, or the pin set was never read. A surface that rendered both as the
   * first would tell a clinician the project does not pin something, on no
   * evidence at all.
   */
  readonly pinSetRead: boolean;
  /**
   * Whether the section reports locally changed values while naming no item
   * that supplied them.
   *
   * THE SERVICE ERASES THE IDENTITY ON PURPOSE. `section_projection.py:765-778`
   * sets `field_origin = None` for every value the draft supplies, and its own
   * `test_a_draft_override_on_an_adopted_field_drops_the_origin` pins that. So
   * an adoption that supplied one field, or one whose every field this project
   * has since overridden, leaves NOTHING in the section response to read an
   * identity from — `sectionAdoptedItems` returns an empty list while
   * `overriddenFieldPaths` does not.
   *
   * That empty list is not the same fact as `not-applicable`. "This section
   * adopts nothing, its configuration comes from the base pack alone" is a
   * statement about the project's configuration, and the response that would
   * support it is exactly the response that cannot be told apart from this one.
   * The honest reading is the ticket's `locally overridden` state plus this
   * flag, so a surface reports that NO SUPPLYING ITEM WAS REPORTED — which is
   * the fact — rather than either that an unnamed item exists or that no item
   * does. Both of those are claims this response cannot carry, and a surface
   * rendering this flag may make neither.
   *
   * It is NOT recovered from the pin set. `AdoptionPin` is `{assetId, version, assetType}`
   * with no section attribution, and `SECTION_BY_ASSET_TYPE` is the library
   * rail's navigation grouping rather than a contract join — attributing a
   * project-wide pin to this section through either would be the browser-side
   * inference the ticket's non-goals rule out. `/adoptions` is authoritative
   * about which pins this project holds and about nothing else, so the block's
   * link there is a place to investigate from, not an answer to which item
   * supplied this section.
   */
  readonly adoptedIdentityErased: boolean;
};

/**
 * One section's whole adoption state, from three server reads.
 *
 * `null` means "could not be read", and it is deliberately not the same value
 * as an empty list: an unread projection must render `unavailable` with a
 * retry, never "this section adopts nothing". Conflating them is the single
 * failure the ticket's own acceptance criteria call out twice.
 *
 * ## PRECEDENCE, AND WHY IT IS THIS ORDER
 *
 * 1. `unavailable` — nothing else can be claimed on top of a read that did not
 *    happen, OR on top of a read that answered without the fact in question. A
 *    projection that did not answer supports no statement about currency; a
 *    catalog that did not answer supports no statement about whether the
 *    library has moved on; and an origin that names no version supports
 *    neither, which is why `version_unreported` settles here rather than
 *    falling through to `current`.
 * 2. `converging` — both project reads answered, but the section projection was
 *    computed from a different pin-set token than the currently loaded pin set.
 *    It may still name an older immutable version, so it must not be reported
 *    as either current or outdated until the reads agree.
 * 3. `not-applicable` — a section that adopts nothing has no version that could
 *    be current or behind, so it settles before either is consulted, and it
 *    settles WITHOUT the catalog: whether the library answered is irrelevant to
 *    a section with no adopted item to name. It requires the section to report
 *    no local change either — see `adoptedIdentityErased`, because an empty
 *    item list beside a non-empty override list is the service withholding an
 *    identity, not a project adopting nothing.
 * 4. `outdated` — of the two remaining states it is the one with a concrete
 *    next step (a newer immutable version exists in the library), so it heads
 *    the block rather than being buried under a standing condition.
 * 5. `overridden` — this project has changed values away from what it adopted.
 * 6. `current` — every adopted item is at the library's head and nothing local
 *    diverges from it.
 *
 * Precedence never HIDES a fact: `overriddenPaths` is returned whatever the
 * headline state, so an outdated-and-overridden section still reports both, and
 * a surface that renders only the headline is choosing to, rather than being
 * unable to.
 */
export function deriveSectionAdoption({
  section,
  catalog,
  pins,
  pinSetVersion,
}: {
  /** The section's projection, or `null` when it could not be read. */
  section: SectionConfig | null;
  /** The catalog naming adopted assets, or `null` when it could not be read. */
  catalog: readonly LibraryAsset[] | null;
  /** This project's pin set, or `null` when it could not be read. */
  pins: readonly AdoptionPin[] | null;
  /** The pin-set token from the same read as `pins`, or `null` when unread. */
  pinSetVersion: string | null;
}): SectionAdoption {
  const sectionAdoptionsVersion = section?.adoptionsVersion ?? null;
  const versionContext = {
    sectionAdoptionsVersion,
    pinSetVersion,
    pinSetRead: pins !== null,
  };

  if (section === null) {
    return {
      state: "unavailable",
      reason: "projection_unread",
      items: [],
      overriddenPaths: [],
      // Unknown rather than guessed: without the response there is no slug to
      // key on, and naming a shape here would be describing a read that failed.
      source: "field_origin",
      ...versionContext,
      adoptedIdentityErased: false,
    };
  }

  const source = adoptionIdentitySource(section.section);
  const items = sectionAdoptedItems(section);
  const overriddenPaths = overriddenFieldPaths(section);

  const byId = new Map((catalog ?? []).map((asset) => [asset.id, asset]));
  const pinnedById = new Map((pins ?? []).map((pin) => [pin.assetId, pin]));

  const views: SectionAdoptedItemView[] = items.map((item) => {
    const asset = byId.get(item.assetId);
    return {
      ...item,
      asset,
      latestVersion: asset?.latestVersion ?? null,
      behind:
        item.version !== null && isBehind(
          { assetId: item.assetId, version: item.version },
          asset,
        ),
      pinnedVersion: pinnedById.get(item.assetId)?.version ?? null,
      pinnedAssetType: pinnedById.get(item.assetId)?.assetType ?? null,
      // `provenance` here is this ITEM's, not the whole project's: the lock the
      // projection reported on the values this item supplied. `adoptionLock`
      // falls back to the catalog's own `lockedFloor` declaration, which is
      // available whether or not an engine is mounted — the reason an unread
      // projection never silently unlocks a floor.
      effectiveLock: adoptionLock(asset, { sections: [], lock: item.lock }),
    };
  });
  const adoptedIdentityErased = items.length === 0 && overriddenPaths.length > 0;

  if (
    sectionAdoptionsVersion !== null &&
    pinSetVersion !== null &&
    sectionAdoptionsVersion !== pinSetVersion
  ) {
    return {
      state: "converging",
      reason: null,
      items: views,
      overriddenPaths,
      source,
      ...versionContext,
      adoptedIdentityErased,
    };
  }

  if (items.length === 0) {
    if (overriddenPaths.length > 0) {
      // The service withheld the identity rather than the project adopting
      // nothing — `field_origin` is dropped for every draft-supplied value, so
      // an adoption whose fields this project has all overridden disappears
      // from the response while its overrides remain in it. Headlining
      // `not-applicable` here would tell a clinician the section runs the base
      // pack alone, which is precisely the response this one cannot be
      // distinguished from. See `SectionAdoption.adoptedIdentityErased`.
      return {
        state: "overridden",
        reason: null,
        items: [],
        overriddenPaths,
        source,
        ...versionContext,
        adoptedIdentityErased: true,
      };
    }

    return {
      state: "not-applicable",
      reason: null,
      items: [],
      overriddenPaths,
      source,
      ...versionContext,
      adoptedIdentityErased: false,
    };
  }

  if (catalog === null) {
    return {
      state: "unavailable",
      reason: "library_unread",
      items: views,
      overriddenPaths,
      source,
      ...versionContext,
      adoptedIdentityErased: false,
    };
  }

  if (views.some((view) => view.asset === undefined)) {
    return {
      state: "unavailable",
      reason: "library_incomplete",
      items: views,
      overriddenPaths,
      source,
      ...versionContext,
      adoptedIdentityErased: false,
    };
  }

  if (views.some((view) => view.behind)) {
    return {
      state: "outdated",
      reason: null,
      items: views,
      overriddenPaths,
      source,
      ...versionContext,
      adoptedIdentityErased: false,
    };
  }

  // AFTER `outdated`, and deliberately. A section holding one item the library
  // has moved past and another whose version was not reported has a concrete,
  // true, actionable headline available — the pin that can be moved — and
  // burying it under an unknown would hide a fact to report a non-fact. Before
  // `overridden` and `current`, because both of those are claims about a
  // version: `current` asserts the project sits on the library's head, and
  // `overridden` (reached with every item at its head) carries the same
  // assertion underneath it. An unnamed version is evidence for neither.
  if (views.some((view) => view.version === null)) {
    return {
      state: "unavailable",
      reason: "version_unreported",
      items: views,
      overriddenPaths,
      source,
      ...versionContext,
      adoptedIdentityErased: false,
    };
  }

  if (overriddenPaths.length > 0) {
    return {
      state: "overridden",
      reason: null,
      items: views,
      overriddenPaths,
      source,
      ...versionContext,
      adoptedIdentityErased: false,
    };
  }

  return {
    state: "current",
    reason: null,
    items: views,
    overriddenPaths,
    source,
    ...versionContext,
    adoptedIdentityErased: false,
  };
}

/**
 * A Clinical configuration rail destination, which is not quite `SectionSlug`.
 *
 * The rail carries the contract's seven sections plus `questionnaires` and
 * `closing`, whose bodies are #369's project-scoped questionnaire detail and
 * #867's Closing conversation entry rather than section projections
 * (`shell/navModel.ts`, `projection: "none"`). Adoption is offered on all nine,
 * so the candidate map below is keyed by the destination a clinician can
 * actually be standing in rather than by the contract union.
 */
export type ClinicalRailSlug = SectionSlug | "questionnaires" | "closing";

/**
 * Which library families a clinician standing in this destination may ADOPT
 * (#489).
 *
 * ## THIS IS A CANDIDATE FILTER, NOT AN ATTRIBUTION
 *
 * `SectionAdoption.adoptedIdentityErased` explains at length why this module
 * refuses to attribute an EXISTING pin to a section: `AdoptionPin` is
 * `{assetId, version, assetType}` with no section attribution, so only the service's own
 * projection can say which item supplies which section, and it does. Nothing
 * here changes that, and nothing here is read to answer it.
 *
 * The question this map answers is a different one, and it is a question the
 * old `/adoptions` surface answered by not asking it: which of the library's
 * families is it useful to offer while the clinician is looking at THIS
 * destination. A wrong entry shows one row too many or too few in a picker; it
 * can never mislabel a pin's provenance, and it cannot change what a write
 * does — `PUT /adoptions` replaces the project's whole pin set and is
 * project-scoped, exactly as it was before #489.
 *
 * ## IT IS THE SERVICE'S OWN GROUPING, NOT THE LIBRARY RAIL'S
 *
 * `services/editor-service/src/editor_service/section_projection.py:925-933`
 * declares `_SUMMARY_SECTION_BY_ASSET_TYPE`, the map #487's clinical summary
 * uses to decide which family belongs under which Project section, and the
 * seven entries it covers are reproduced here unchanged. The contract carries
 * the same fact for the two refs-only sections in their shapes:
 * `AlertsSection.matrices` is `AlertMatrixRef[]` and `PathwaysSection.pathways`
 * is `PathwayRef[]`.
 *
 * The two the service map does not state are stated here for reasons of their
 * own. `summary_prompt` is absent from it only because
 * `ClinicalSummarySectionSlug` is deliberately narrower than `SectionSlug` —
 * the contract says so in as many words — and `questionnaire` because
 * `questionnaires` is not a `SectionSlug` at all; `ProjectQuestionnaires`
 * already filters this project's pins by exactly that type.
 *
 * `satisfies` makes both closures compile-time: a new rail destination, or a
 * tenth `LibraryAssetType` in a regenerated contract, fails here rather than
 * becoming silently unadoptable — which is the defect #489's first review round
 * found, arrived at by deletion rather than by omission.
 *
 * #612 adds a SECOND consumer, and it is still not an attribution of supply:
 * `storedAdoptionRows` below uses this map to say which stored pins a section
 * SURFACE renders when the composed summary cannot answer. That claim — "this
 * project pins this asset, and the service groups this family under this
 * section" — is exactly the claim the service's own
 * `_SUMMARY_SECTION_BY_ASSET_TYPE` makes when it composes the summary, so the
 * two sources cannot disagree about which section a pin's row belongs to. What
 * remains browser-inference, and remains forbidden, is the claim
 * `adoptedIdentityErased` documents: WHICH FIELDS a pin supplied. No stored row
 * makes it.
 */
export const ADOPTABLE_ASSET_TYPES_BY_SECTION = {
  // BOTH BG families, on the service's own grouping
  // (`_SUMMARY_SECTION_BY_ASSET_TYPE`): the shared interview profile (#858) is
  // read WITH the domains, so it belongs to the same Background surface. The
  // named profile control the Lovable reference shows is `bg_profile_ui`'s
  // (#914); until it lands the profile is an ordinary adopted row here, which
  // is what keeps a pinned profile visible and removable rather than a pin no
  // surface renders.
  "bg-sets": ["background_set", "background_profile"],
  "chief-complaint": ["main_concern"],
  pathways: ["pathway", "pathway_variant"],
  // BOTH Rules families (#870), on the service's own grouping
  // (`_SUMMARY_SECTION_BY_ASSET_TYPE`): a speech profile is read in the Rules
  // section beside the language content, which is where the S7 screen contract
  // puts it. It is a SEPARATE row and a SEPARATE pin, never a field of the
  // Rule's — `placement_registry` bounds the two independently ("adopting a
  // Rule neither supplies nor consumes a speech profile") and they place
  // different files. Without this entry a pinned profile is a pin no surface
  // renders and no clinician can remove.
  rules: ["language_rules", "speech_profile"],
  safety: ["safety_track"],
  alerts: ["alert_matrix"],
  summary: ["summary_prompt"],
  questionnaires: ["questionnaire"],
  // #867. `closing_conversation` is grouped under its own destination rather
  // than folded into `pathways` or `summary`: the S7 screen contract places it
  // "after Questionnaires, before Summary" and says outright that it must never
  // count as a diagnostic Always/Dynamic pathway. Adopting it takes the
  // `closing-activation` lane, which `activationLaneFor` derives from the family
  // rather than from this map.
  closing: ["closing_conversation"],
} as const satisfies Record<ClinicalRailSlug, readonly LibraryAssetType[]>;

export function adoptableAssetTypes(
  section: ClinicalRailSlug,
): readonly LibraryAssetType[] {
  return ADOPTABLE_ASSET_TYPES_BY_SECTION[section];
}

/**
 * The library section a clinician standing in this rail destination browses
 * (#615).
 *
 * The same candidate-filter caveat as the map above: this is a NAVIGATION
 * destination — which catalog page it is useful to open from this section —
 * never an attribution of any pin. Derived from the section's own adoptable
 * families through `SECTION_BY_ASSET_TYPE` rather than written as a second
 * hand-maintained map, so the two facts cannot drift: a section's browse
 * destination is always where its adoptable families are catalogued, and
 * sections whose families share one library section (`safety` and `alerts`
 * both catalogue under `safety-paths`) converge there by that derivation.
 */
export function librarySectionForRailSlug(
  section: ClinicalRailSlug,
): LibrarySectionSlug {
  return sectionForAssetType(ADOPTABLE_ASSET_TYPES_BY_SECTION[section][0]);
}

/**
 * The library items this destination may offer to adopt, in display order — or
 * `null` where the question could not be answered at all.
 *
 * ## `null` IS NOT AN EMPTY LIST, HERE AS EVERYWHERE ELSE IN THIS MODULE
 *
 * `deriveSectionAdoption` states the rule this now follows: a read that did not
 * happen is `null`, and `null` is deliberately not the same value as an empty
 * list, because "could not be read" and "there is nothing" are different facts
 * and only one of them is a statement about the project.
 *
 * The first version of this function returned `[]` for both, and a surface
 * cannot recover a distinction its input has already discarded. A paused
 * catalog with nothing cached produced an empty list, and the block rendered
 * "the shared clinical library offers nothing further to adopt" — an absence
 * claim on no evidence, standing next to the recovery notice that said the
 * catalog had not been read (Codex, #511 round 2).
 *
 * Both reads are required, for different reasons. Without the CATALOG there is
 * nothing to enumerate. Without the PIN SET, "this project does not pin it" is
 * an assertion on no evidence, and offering to adopt something the project
 * already holds would open a confirmation describing a move as an add.
 *
 * ARCHIVED ITEMS ARE LISTED, not filtered out. The catalog is read with
 * `includeArchived`, `adoptionRefusal` disables the control with the service's
 * own archived rule, and a clinician who cannot find an item they know exists
 * learns nothing from its absence. This is the behaviour `/adoptions` had.
 *
 * Order is type label then name, which is `ProjectAdoptions`' own ordering: an
 * asset id is opaque, so the wire order is arbitrary and would reshuffle
 * between reads.
 */
export function sectionAdoptionCandidates({
  section,
  catalog,
  pins,
}: {
  section: ClinicalRailSlug;
  /** The catalog, archived rows included, or `null` when it could not be read. */
  catalog: readonly LibraryAsset[] | null;
  /** This project's pin set, or `null` when it could not be read. */
  pins: readonly AdoptionPin[] | null;
}): readonly LibraryAsset[] | null {
  if (catalog === null || pins === null) {
    return null;
  }

  const types = new Set<string>(adoptableAssetTypes(section));
  const pinned = new Set(pins.map((pin) => pin.assetId));

  return catalog
    .filter((asset) => types.has(asset.assetType) && !pinned.has(asset.id))
    .sort(
      (left, right) =>
        ASSET_TYPE_LABELS[left.assetType].localeCompare(
          ASSET_TYPE_LABELS[right.assetType],
        ) || left.name.localeCompare(right.name),
    );
}

/**
 * One section's stored pins as compact review rows, from the pin set and the
 * catalog alone (#612).
 *
 * ## WHY THIS EXISTS: A FAILED COMPOSITION MUST NOT ERASE THE PIN LIST
 *
 * The 2026-08-28 parity audit reproduced a P0 dead end on Main concern, Rules
 * and Safety (`MC-PRJ-07`, `RULE-PRJ-05`, `SAFE-PRJ-06`): an authored pin the
 * runtime cannot place makes composition answer 409, the review surface
 * rendered its rows FROM that composition, and the failure state therefore
 * removed the exact `Remove from project` action the server's own diagnosis
 * instructs the clinician to use. Pathways kept its row only because its
 * projection is refs-only server-side and never runs the engine dump
 * (`PW-PRJ-11`). The ratified rule is the one the BG-set contract already
 * states: Project rows are read from the catalog plus stored adoptions, not
 * reconstructed from section projections — so the row a recovery action hangs
 * off must be derivable exactly as `BgSetAdoptionPicker` derives its rows,
 * with no engine in the dependency set.
 *
 * ## WHAT ONE OF THESE ROWS CLAIMS, AND WHAT IT DOES NOT
 *
 * A stored row claims "this project pins this exact immutable version, and the
 * service groups this family under this section" — both facts read from server
 * responses (`GET /adoptions`, the catalog's `assetType`, and the service's
 * own summary grouping mirrored by `ADOPTABLE_ASSET_TYPES_BY_SECTION`). It
 * does NOT claim the pin composed, supplied fields, or validated: it renders
 * `supplyState: "adopted"` with the pin's own version and nothing derived.
 * That honesty is the point — the audited failure is precisely a pin that is
 * stored and cannot compose, and the stored row is the true statement about it.
 *
 * ## A STORED ROW IS REMOVAL-ONLY: NO HEAD, NO "OUTDATED", NO ADOPT.
 * `latestVersion` is `null` and `attention` is empty, deliberately, even though
 * the catalog names a head. `GET /adoptions` and the catalog are two reads at
 * two moments, and a publish-and-pin landing between them leaves the catalog's
 * head OLDER than the pin. Inequality against that head would flag a fresh v2
 * pin as outdated and hand `itemActions` "v1" to offer as `Adopt v1` — a
 * downgrade the whole-set CAS write cannot refuse, because the token it
 * carries is current. The composed summary is the ONE response that joins a
 * pin to its head server-side (`itemActions` reads its `latestVersion` for
 * exactly this reason), so an update is offered only from a composed row; a
 * stored row offers what its data can vouch for — open in the library, or
 * remove — and `itemActions`' "no version, no Adopt" rule keeps it that way
 * without a second guard. Offering updates in the failure state would need a
 * server-produced snapshot of pin and head read together.
 *
 * ## THE LOCK COMES FROM THE CATALOG, so an unreadable projection never
 * silently unlocks a floor — `adoptionLock(asset, undefined)` falls back to
 * `capabilities.lockedFloor`, which is derived server-side and available
 * whether or not an engine is mounted. A locked-floor pin therefore renders,
 * visibly locked, with removal refused and explained (`SAFE-PRJ-01/02`: the
 * floor stays non-removable in every state).
 *
 * ## THE SECTION COMES FROM THE PIN, AND SO DOES THE ROW'S EXISTENCE (#615).
 * Attribution to a section requires the asset's family, and since #615 the
 * pin set states it: `AdoptionPin.assetType` is the catalog's fact joined onto
 * the pin server-side, from the column the composite FK to
 * `library_assets(id, asset_type)` keeps consistent. So a stored row is
 * derivable from `GET /adoptions` ALONE, and the catalog only enriches it —
 * the name and the lock. The recovery contract requires exactly that: every
 * composition failure must still expose the exact stored pin (parity
 * authority, "Asset placement and recovery"), and a pin the catalog cannot
 * currently name is not exempt. A pin the catalog does not name — because the
 * catalog was not read, or answered without it — therefore renders a RECOVERY
 * ROW carrying what the pin set vouches for and nothing guessed: the opaque id
 * in place of a name the library did not give, the pin's own family and exact
 * version, and no lock claim in either direction. Its exact library route is
 * built from that family, which is the whole reason the pin carries it (PR
 * #663 review: the first cut dropped the row whenever the catalog could not
 * name it, so a composition failure and a catalog outage arriving together
 * erased the required row with the exact pin known all along). The catalog is
 * read with `includeArchived`, so none of this is the archived case — archived
 * pins carry their catalog name and stay removable, exactly as the
 * `PUT /adoptions` contract keeps them writable ("kept without being
 * re-decided … may be kept or removed").
 *
 * WHAT A RECOVERY ROW MAY DO IS DECIDED ELSEWHERE, AND FAILS CLOSED. This
 * function renders; `SectionReview` decides the controls, on the dialog's own
 * rule: no write control at all until BOTH the pin set and the catalog
 * settled, and for a pin the settled catalog does not list,
 * `adoptionRefusal`'s unknown-asset rule refuses and states it. A locked
 * floor whose catalog row is unread is thus non-removable by the first gate,
 * not by a lock this function would have to invent.
 *
 * Order is the pin set's own — the server's stored order, stable across reads.
 */
export function storedAdoptionRows(
  section: ClinicalSummarySectionSlug,
  pins: readonly AdoptionPin[],
  /** The catalog, archived included — or `null` when it was not read. */
  catalog: readonly LibraryAsset[] | null,
): readonly ClinicalSummaryItem[] {
  const types = new Set<string>(adoptableAssetTypes(section));
  const byId = new Map((catalog ?? []).map((asset) => [asset.id, asset]));

  return pins.flatMap((pin) => {
    // The PIN's family decides the section, never the catalog row's: the two
    // agree by the composite FK, and only the first is available when the
    // catalog is not.
    if (!types.has(pin.assetType)) {
      return [];
    }
    const asset = byId.get(pin.assetId);
    const lock = adoptionLock(asset, undefined);
    return [
      {
        id: pin.assetId,
        // The catalog's name, or the exact id the pin set holds — an opaque
        // token rather than a guessed label, so the row never claims a name
        // the library did not give it.
        name: asset?.name ?? pin.assetId,
        family: pin.assetType,
        section,
        source: {
          kind: "library_adoption" as const,
          ref: pin.assetId,
          version: pin.version,
        },
        supplyState: "adopted" as const,
        adoptedVersion: pin.version,
        // Never the catalog's head: the two reads are not atomic, and a head
        // read before a concurrent publish-and-pin would present a downgrade
        // as "Newer version available". See the module note above.
        latestVersion: null,
        provenance: "adopted" as const,
        lock: lock
          ? { state: "locked" as const, reasons: [lock] }
          : { state: "editable" as const, reasons: [] },
        attention: [],
      },
    ];
  });
}

/**
 * Which rows the section review body renders, decided in ONE place (#612).
 *
 * ## WHY THE DECISION IS EXPORTED RATHER THAN LIVED IN THE COMPONENT
 *
 * Two surfaces have to agree about it, and they used to agree by one mirroring
 * the other's branch condition in a comment. `SectionReview` renders the rows;
 * `SectionDiagnostics` tells `SectionAdoptionPanel` which item ids those rows
 * cover, so the panel offers no second update/remove control for them (Codex,
 * #570 round 2). With #612 the row set is no longer a plain read of the
 * composed summary — a failed, pending or empty composition falls back to the
 * stored pin set — and a mirrored condition would now be a compound rule
 * restated in two files. One function, called by both, is the same cure the
 * dialog's `adoptionDecisionReady` export applied to the same class of drift.
 *
 * ## THE PRECEDENCE
 *
 * 1. A successful composition that carries items is authoritative and renders
 *    exactly as before — the composed row is richer (provenance, attention,
 *    base-supplied entries) and is the service's own account of what supplies
 *    the section. Nothing about the success path changes under #612.
 * 2. Everything else — the summary pending, failed (409/5xx/paused), or
 *    composed empty — falls back to `storedAdoptionRows`. For a genuinely
 *    empty section with no pins that is an empty list, and the caller renders
 *    its empty state exactly as before. For the audited dead end it is the
 *    stored pin whose removal is the recovery.
 *
 * `summary`/`pins`/`catalog` arrive `null` when their read has not SETTLED
 * SUCCESSFULLY — `settledRead` below, which is `adoptionDecisionReady`'s own
 * `isSuccess` rule applied to the inputs, so the two callers cannot feed this
 * function different facts. The two nulls of the fallback mean different
 * things here:
 *
 *   * an unread PIN SET yields no stored row, because there is no pin to
 *     render — a row over a pin set this surface cannot vouch for would be an
 *     affordance over nothing. The panel above names the failed read and
 *     offers its retry.
 *   * an unread CATALOG does NOT (#615, PR #663 review): the pin set alone
 *     names each pin's id, exact version and family, and `storedAdoptionRows`
 *     renders the recovery row from exactly that. The audited composition
 *     failure and a catalog outage arriving together would otherwise erase
 *     the one row the recovery contract requires. What the missing catalog
 *     withholds is decided where the controls are — `SectionReview` offers no
 *     write control until both reads settled — not by dropping the row.
 */
export type SectionReviewRows = {
  /** Which source produced the rows — composed summary, or stored pin set. */
  readonly source: "composed" | "stored";
  /**
   * Whether these rows describe an EARLIER pin set than the loaded one (#618).
   *
   * The same fact `deriveSectionAdoption` reports as `state: "converging"` for
   * the block, read off the third project response rather than the second:
   * `ClinicalSectionSummary.adoptionsVersion` against the `adoptionsVersion`
   * that came with `pins`. Its consumers render an explicit converging
   * treatment — the row's version withheld and no adopt or update control —
   * rather than presenting a version that a settling read may already have
   * moved past.
   *
   * ALWAYS FALSE FOR STORED ROWS, and not by omission: a stored row is built
   * from `pins` themselves, so its version cannot disagree with the token that
   * arrived with it.
   */
  readonly converging: boolean;
  readonly items: readonly ClinicalSummaryItem[];
};

/**
 * ## THE CONVERGING GATE LIVES HERE, NOT IN A CONSUMER (#618)
 *
 * The audited overlap (`SUM-PRJ-08`) is two surfaces on one screen naming
 * different adopted versions as authoritative for 1017 ms after an exact pin
 * move. #673 closed the block's half by giving `GET /sections/{section}` a
 * pin-set token and gating on it; the rows are fed by a THIRD read,
 * `GET /section-summaries/{section}`, which now carries the same token.
 *
 * The comparison belongs in this function for the reason the function exists:
 * `SectionReview` renders the rows and `SectionDiagnostics` derives the panel's
 * silence set from the same call, and a rule restated in two files is the drift
 * the export was created to prevent. A converging row therefore stays IN the
 * row set — so it stays in `canonicalItemIds`, and the panel above does not
 * start offering a second control for an item whose row is deliberately
 * withholding one.
 *
 * `pinSetVersion` absent means the comparison cannot be made, never that it
 * passed: an unread pin set leaves the rows exactly as they rendered before
 * this gate existed, which is the same posture `deriveSectionAdoption` takes
 * (`unavailable`/no-converge rather than an invented verdict). A summary
 * carrying no token at all — an older service behind a newer client — is read
 * the same way, so a client cannot lock every row into converging against a
 * deployment that never answers the question.
 */
export function sectionReviewRows({
  summary,
  pins,
  pinSetVersion,
  catalog,
  section,
}: {
  /** The composed summary, or `null` when it has not successfully answered. */
  summary: ClinicalSectionSummary | null;
  /** The pin set, or `null` when it has not successfully answered. */
  pins: readonly AdoptionPin[] | null;
  /** The pin-set token from the SAME read as `pins`, or `null` when unread. */
  pinSetVersion: string | null;
  /** The catalog (archived included), or `null` when not successfully read. */
  catalog: readonly LibraryAsset[] | null;
  section: ClinicalSummarySectionSlug;
}): SectionReviewRows {
  if (
    summary !== null &&
    summary.availability !== "empty" &&
    summary.items.length > 0
  ) {
    const summaryAdoptionsVersion = summary.adoptionsVersion ?? null;
    return {
      source: "composed",
      converging:
        summaryAdoptionsVersion !== null &&
        pinSetVersion !== null &&
        summaryAdoptionsVersion !== pinSetVersion,
      items: summary.items,
    };
  }
  if (pins === null) {
    return { source: "stored", converging: false, items: [] };
  }
  return {
    source: "stored",
    converging: false,
    items: storedAdoptionRows(section, pins, catalog),
  };
}

/**
 * One read's settled answer, or `null` — the input rule of `sectionReviewRows`.
 *
 * `isSuccess` rather than "has data", for the reason `adoptionDecisionReady`
 * states: TanStack keeps `.data` when a REFETCH fails, and a row set derived
 * from a cached answer the read itself now reports as failed would disagree
 * with the controls gated on that read. Exported so `SectionReview` (which
 * renders the rows) and `SectionDiagnostics` (which silences the panel for
 * their ids) derive their inputs through ONE function — the ids the panel
 * withholds must be exactly the rows the body renders, and two hand-written
 * ternaries were the drift this replaces.
 */
export function settledRead<T>(read: {
  readonly isSuccess: boolean;
  readonly data: T | undefined;
}): T | null {
  return read.isSuccess && read.data !== undefined ? read.data : null;
}
